import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Quote,
  ArrowUpRight,
  ShieldCheck,
  Target,
  Building2,
  Globe2,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const heroBg = "/assets/images/md.jpeg";
const sectionBg = "/assets/images/bg2.jpeg";
const mdImage = "/assets/images/md2.jpeg";

const focusAreas = [
  {
    title: "Efficiency & Trust",
    text:
      "Strengthening institutional confidence, transparency and operational efficiency across all OGFZA activities.",
    icon: ShieldCheck,
  },
  {
    title: "Infrastructure Excellence",
    text:
      "Driving infrastructure modernization to support globally competitive oil and gas free zones.",
    icon: Building2,
  },
  {
    title: "Investor Focus",
    text:
      "Building a customer-centric environment that promotes investor satisfaction and sustainable partnerships.",
    icon: Globe2,
  },
  {
    title: "Regulatory Modernization",
    text:
      "Aligning OGFZA operations with international best practices and modern free zone standards.",
    icon: Target,
  },
];

const MDMessage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.8,
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        delay: 0.2,
        duration: 0.9,
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 55,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 86%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el, index) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          delay: index * 0.05,
          duration: 0.75,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden text-white">
        <img
          src={heroBg}
          alt="MD Message"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#041018]/75" />

        <div className="absolute inset-0 bg-gradient-to-br from-[#082567]/85 via-[#082567]/55 to-[#026B07]/25" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#F2AF11]" />
            Leadership Message
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            MD’s Message
          </h1>

          <p className="hero-text mt-8 max-w-3xl text-white/85 text-lg leading-[1.9]">
            A message from the Managing Director of the Oil and Gas Free Zones
            Authority on the future of investment, infrastructure and free zone
            development in Nigeria.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#F2AF11] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <Link
              to="/about"
              className="hover:text-[#F2AF11] transition-colors"
            >
              About Us
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#F2AF11]">MD’s Message</span>
          </div>
        </div>
      </section>

      {/* MD SECTION */}
      <section className="relative py-24 bg-white overflow-hidden">
        <img
          src={sectionBg}
          alt="Background"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.05]"
        />

        <div className="absolute inset-0 bg-white/95" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.78fr_1.22fr] gap-14 items-start">
          {/* IMAGE */}
          <div className="reveal-up lg:sticky lg:top-24">
            <div className="relative rounded-[32px] overflow-hidden border border-slate-200 shadow-[0_24px_80px_-50px_rgba(8,37,103,0.45)] bg-white p-3">
              <img
                src={mdImage}
                alt="Managing Director"
                className="w-full h-[620px] object-cover rounded-[24px]"
              />

              <div className="absolute inset-3 rounded-[24px] bg-gradient-to-t from-[#082567]/85 via-transparent to-transparent" />

              <div className="absolute bottom-7 left-7 right-7">
                <div className="bg-white rounded-[22px] p-5 shadow-xl">
                  <p className="text-[#026B07] font-black tracking-[0.18em] uppercase text-[11px] mb-2">
                    Managing Director / CEO
                  </p>

                  <h3
                    className="text-[#082567] text-2xl font-black leading-tight"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    Bamanga Usman Jada
                  </h3>
                </div>
              </div>
            </div>
          </div>

          {/* MESSAGE */}
          <div className="reveal-up">
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full bg-[#FAFBFC] border border-slate-200 mb-7">
              <Quote size={18} className="text-[#F2AF11]" />

              <span className="text-[#082567] font-bold text-sm">
                Message From The Managing Director
              </span>
            </div>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Positioning OGFZA For Global Investment Leadership
            </h2>

            <div className="space-y-6 text-slate-700 leading-[1.9] text-[15.5px]">
              <p>
                As Managing Director of the Oil and Gas Free Zones Authority,
                I remain committed to aligning OGFZA with the economic
                transformation agenda of the Federal Government of Nigeria.
              </p>

              <p>
                Our objective is to reposition Nigeria’s Oil and Gas Free Zones
                as globally competitive investment destinations capable of
                attracting strategic partnerships, industrial development and
                sustainable economic growth.
              </p>

              <p>
                We are focused on establishing free zones across the country,
                modernizing infrastructure, strengthening investor confidence,
                adopting international best practices and improving customer
                experience across all operational areas.
              </p>

              <p>
                OGFZA is also committed to introducing innovative systems,
                supporting local content development, improving operational
                transparency and building stronger institutional capacity.
              </p>

              <p>
                Through collaboration with stakeholders, investors, operators
                and government institutions, we are creating an enabling
                environment that supports long-term growth and national
                development.
              </p>

              <p>
                I sincerely appreciate the support of our partners and assure
                all investors of OGFZA’s continued dedication to excellence,
                professionalism and service delivery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="relative py-24 bg-[#FAFBFC]">
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#082567_1px,transparent_1px),linear-gradient(to_bottom,#082567_1px,transparent_1px)] bg-[size:70px_70px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="reveal-up max-w-4xl mx-auto text-center mb-14">
            <p className="text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Strategic Focus
            </p>

            <h2
              className="text-4xl md:text-5xl font-black text-[#082567] leading-tight"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Key Priorities Driving OGFZA Forward
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {focusAreas.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="card-animate bg-white rounded-[28px] p-8 border border-slate-200 hover:-translate-y-2 transition-all duration-500 hover:shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)] hover:border-[#F2AF11]/60"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#082567]/5 text-[#082567] border border-[#082567]/10 flex items-center justify-center mb-6">
                    <Icon size={25} />
                  </div>

                  <h3
                    className="text-2xl font-black text-[#082567] leading-tight mb-4"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-[1.8] text-[15.5px]">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal-up rounded-[36px] border border-slate-200 bg-[#FAFBFC] p-8 md:p-12 shadow-[0_24px_80px_-55px_rgba(8,37,103,0.55)]">
            <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-3 text-[#026B07] font-black tracking-[0.2em] uppercase text-xs mb-5">
                  <ShieldCheck size={18} className="text-[#F2AF11]" />
                  Investor Confidence
                </div>

                <h2
                  className="text-4xl md:text-5xl font-black text-[#082567] leading-tight mb-5"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Building Sustainable Investment Growth Through Leadership &
                  Innovation
                </h2>

                <p className="text-slate-600 leading-[1.9] text-[16px] max-w-4xl">
                  OGFZA remains committed to operational excellence,
                  modernization, investor satisfaction and the continued growth
                  of Nigeria’s Oil and Gas Free Zones.
                </p>
              </div>

              <Link
                to="/about/incentives"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-[#082567] hover:bg-[#F2AF11] hover:text-[#082567] text-white font-black transition-all duration-500 whitespace-nowrap"
              >
                Explore Incentives
                <ArrowUpRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MDMessage;